import { useEffect, useRef, useState } from "react";

/**
 * TraceCard — enveloppe "carte animée" réutilisable (style capeo) :
 *   • liseré coloré qui trace/tourne autour de la carte au survol
 *   • soulèvement + ombre au survol
 *   • apparition en fondu + glissement au scroll (stagger via `index`)
 *
 * Fournit le chrome de la carte via `className` (bord, fond, arrondi, padding).
 * La CSS du liseré (.em-trace-card / .em-trace-line) est dans index.css.
 *
 * Props : accent (couleur), index (ordre pour le stagger), radius (px, doit
 * correspondre à l'arrondi de la carte), className, children.
 */
export default function TraceCard({ accent = "#2E7D62", index = 0, radius = 16, className = "", children }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTimeout(() => setShown(true), (index % 4) * 120);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      style={{ "--seg-c": accent }}
      className={`em-trace-card group relative transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      <svg
        aria-hidden
        className="em-trace-line pointer-events-none absolute inset-0 z-10 h-full w-full"
        preserveAspectRatio="none"
      >
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          rx={radius}
          ry={radius}
          fill="none"
          stroke="var(--seg-c)"
          strokeWidth="2.5"
          pathLength="100"
          strokeDasharray="22 78"
          strokeDashoffset="0"
          strokeLinecap="round"
        />
      </svg>
      {children}
    </div>
  );
}
